import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { peopleCoreAPI } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import type { TimeAdjustmentRequest } from '../types';

export function useAdjustmentRequests(
  params?: {
    status?: string;
    startDate?: string;
    endDate?: string;
    employeeId?: string;
  },
  options?: Omit<UseQueryOptions<TimeAdjustmentRequest[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TimeAdjustmentRequest[]>({
    queryKey: ['people-core', 'adjustment-requests', params],
    queryFn: async () => {
      const res = await peopleCoreAPI.listAdjustmentRequests(params);
      return res.data.data ?? [];
    },
    ...options,
  });
}

export function useAdjustmentRequestMutations() {
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['people-core', 'adjustment-requests'] });

  const create = useMutation({
    mutationFn: (data: {
      date: string;
      requestedData: { clockIn?: string; clockOut?: string; breakStart?: string; breakEnd?: string };
      justification: string;
    }) => peopleCoreAPI.createAdjustmentRequest(data),
    onSuccess: () => {
      toast.success('Solicitação enviada! Aguardando aprovação.');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const review = useMutation({
    mutationFn: ({ id, ...data }: { id: string; status: 'APPROVED' | 'REJECTED'; reviewNote?: string }) =>
      peopleCoreAPI.reviewAdjustmentRequest(id, data),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Solicitação revisada!');
    },
    onError: (err: Error) => toast.error(err.message),
    onSettled: () => {
      // Invalida sempre (sucesso ou erro) — garante consistência mesmo se
      // o backend tiver falhado após salvar o status
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['people-core', 'time-entries'] });
      queryClient.invalidateQueries({ queryKey: ['people-core', 'timesheet'] });
    },
  });

  return { create, review };
}
